/* eslint-disable sonarjs/cognitive-complexity */
import { handleFlags } from '@helpers/algolia/product-hit-adapter';
import { setCookieWithDomain } from '@helpers/cookies';
import debounce from '@helpers/debounce';
import trackListView from 'event-tracks/list-view-analytics';
import { trackPLAListViewed, trackProductListViewed } from 'event-tracks/product-analytics';
import { trackSponsoredImpressionNewtail } from 'event-tracks/sponsored';
import { setCookie } from 'nookies';
import { TListEventsOrigin, TListViewedProps } from 'types/analytics/product/product.types';

export function productClickTrackAdapter(hit: Record<string, any>, indexName: string): any {
  const arrayFlags = extractFlags(hit);
  const variantesHits = hit?.variantes?.Cor ? Object.keys(hit?.variantes?.Cor).toString() : '';

  let variantes = [];

  if (hit.variantes) {
    if (Array.isArray(hit?.variantes)) {
      variantes = hit.variantes;
    } else if (Object.keys(hit.variantes).length > 0) {
      variantes = Object.keys(hit.variantes[Object.keys(hit.variantes)[0]]);
    }
  }

  const isAvailability = hit?.mini_buy_box[0].estoque > 0;
  const productId = parseInt(hit.objectID, 10);
  const eanHit = hit.ean ? hit.ean.toString() : '';

  return {
    availability: isAvailability,
    average_rating: hit?.extra?.reviews?.rate_rounded || 0,
    brand: hit?.marca || 'Não identificado',
    card_discount: null,
    card_price: hit?.mini_buy_box?.preco_promocional || 0,
    category: hit?.nivel_1 || 'Não identificado',
    coupon: 'Não identificado',
    currency: 'BRL',
    discount_tag: hit?.percentual_desconto_sku || null,
    ean: eanHit,
    full_installment_price: null,
    image_url: hit?.imagens || [],
    index: indexName || 'Não identificado',
    name: hit?.nome || 'Não identificado',
    objectID: hit?.objectID || 'Não identificado',
    original_price: hit?.preco_de || null,
    // eslint-disable-next-line no-underscore-dangle
    position: hit.__position,
    price: hit?.preco_por || null,
    product_id: productId || null,
    product_matrix: 0,
    product_tag: arrayFlags,
    product_type: hit?.mini_buy_box?.[0]?.id_seller === 1 ? '1P' : '3P',
    product_new_category: hit?.product_hub?.commercial_category || 'Sem Categoria',
    quantity: 0,
    // eslint-disable-next-line no-underscore-dangle
    queryID: hit.__queryID,
    reviews_quantity: hit?.extra?.reviews ? hit.extra.reviews.total_opinions : null,
    seller: hit?.mini_buy_box[0].seller_slug || null,
    seller_id: hit?.mini_buy_box[0].id_seller || null,
    shipping_method: 'Não identificado',
    sku: hit.id_produto || null,
    ticket_price: 0,
    total_variants: variantes?.length,
    url: hit.url || null,
    variant: variantesHits,
    variant_position: 0,
    warranty_months: hit?.meses_garantia_fabricante || null,
    sponsoredData: hit?.sponsoredData || null,
  };
}
function extractProductCategory(hit: Record<string, any>) {
  return Object.entries(hit)
    .filter(e => e[0]?.startsWith('nivel_'))
    .sort((a: any, b: any) => parseInt(a[0]?.match(/\d+/), 10) - parseInt(b[0]?.match(/\d+/), 10))
    .filter(a => Boolean(a[1]))
    .pop()
    ?.pop();
}

export function productListTrackAdapter(hit: Record<string, any>): any {
  const arrayFlags = extractFlags(hit);
  const variantesHits = hit.variantes?.Cor ? Object.keys(hit.variantes?.Cor).toString() : '';
  const skuHit = hit.id_produto.toString();
  const eanHit = hit.ean ? hit.ean.toString() : '';

  let totalVariantes = 0;

  if (hit.variantes) {
    if (Array.isArray(hit.variantes)) {
      totalVariantes = hit.variantes?.length;
    } else if (Object.keys(hit.variantes).length > 0) {
      totalVariantes = Object.keys(hit.variantes[Object.keys(hit.variantes)[0]]).length;
    }
  }

  return {
    average_rating: hit?.extra?.reviews?.rate_rounded || 0,
    brand: hit?.marca,
    category: hit?.nivel_1,
    coupon: 'Não identificado',
    currency: 'BRL',
    discount_tag: hit?.percentual_desconto_sku,
    ean: eanHit,
    image_url: hit?.imagens?.[0] || 'Não identificado',
    name: hit?.nome,
    objectID: hit?.objectID,
    // eslint-disable-next-line no-underscore-dangle
    position: hit.__position,
    price: hit?.preco_por,
    product_id: hit?.objectID,
    product_matrix: 'Não identificado',
    product_tag: arrayFlags,
    product_type: hit?.mini_buy_box?.[0]?.id_seller === 1 ? '1P' : '3P',
    quantity: 0,
    reviews_quantity: hit?.extra?.reviews ? hit?.extra.reviews.total_opinions : 0,
    shipping_method: 'Não identificado',
    sku: skuHit,
    total_variants: totalVariantes,
    url: hit?.url,
    variant: variantesHits,
    variant_position: 'Não identificado',
    warranty_months: hit?.meses_garantia_fabricante,
    product_seller: hit?.nome_seller,
    product_seller_id: hit?.mini_buy_box?.[0]?.id_seller ?? null,
    product_category_id: hit?.id_categoria,
    product_category_tree: hit?.slug?.[0].url_listagem,
    product_category: extractProductCategory(hit),
    product_new_category: hit?.product_hub?.commercial_category || 'Sem Categoria',
    // eslint-disable-next-line no-underscore-dangle
    product_alg_query_id: hit?.__queryID,
    sponsoredData: hit?.sponsoredData || null,
  };
}

export const debouncedHitsRender = debounce(
  (
    {
      hits,
      index,
      title,
      adapterQueryId,
      pageTitle,
      enabledSegmentTrack = true,
      listIdName,
      isFiltered,
      listShippingFilter,
    },
    eventsOrigin: TListEventsOrigin
  ) => {
    if (adapterQueryId?.queryID) {
      setCookieWithDomain({
        cookie: 'queryID',
        value: adapterQueryId?.queryID,
      });
    }

    if (index) {
      setCookieWithDomain({
        cookie: 'algIndex',
        value: index,
      });
    }

    if (index) {
      setCookie(null, 'algIndex', index, {
        path: '/',
        domain: '.madeiramadeira.com.br',
      });
    }

    if (hits.length > 0 && enabledSegmentTrack && typeof window !== 'undefined') {
      const eventData: TListViewedProps = {
        list_id: eventsOrigin === 'PLA' ? 'PLA' : null,
        list_filtered: isFiltered,
        category: pageTitle || title,
        index,
        event_date: new Date(),
        products: hits,
        queryID: adapterQueryId.queryID,
      };

      if (eventsOrigin !== 'PLA') {
        trackListView({
          index,
          products: hits,
          listId: listIdName,
          isFiltered,
          listShippingFilter,
        });
        trackSponsoredImpressionNewtail(hits);
      }

      if (eventsOrigin === 'PLA') {
        return trackPLAListViewed(eventData);
      }
      return trackProductListViewed(eventData);
    }
    return null;
  },
  2000
);

function extractFlags(hits) {
  const flags = handleFlags(hits);
  const arrayFlags = [];

  Object.keys(flags).forEach(item => {
    if (flags[item].value === true) {
      arrayFlags.push(item);
    }
  });
  return arrayFlags;
}
